import {
	buttonLoading,
	initPage,
	totalPages,
	totalRecords,
} from "src/store/store";
import { extractErrorMessage, mapDataToAddressOptions } from "src/utils/misc";
import client from "../client";

export async function lockUnlockCar(action, vehicleId, remark) {
	try {
		buttonLoading.set(true);
		const response = await client(`/v1/${action}/${vehicleId}`, {
			method: "POST",
			version: "Vehicle",
			data: {
				remark,
			},
		});
		return response;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	} finally {
		buttonLoading.set(false);
	}
}

// Same endpoint CarManagementTable's status dropdown uses
// (see changeCarStatus there) — statusId must match a `lookups.vehicleStatuses`
// entry's id (fetched via `fetchTaskLookups` from src/services/lookupCache.ts).
export async function changeVehicleStatus(vehicleId, statusId) {
	try {
		buttonLoading.set(true);
		const response = await client(`/v2/${vehicleId}/status`, {
			method: "PUT",
			version: "Vehicle",
			data: {
				statusId,
			},
		});
		return response;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	} finally {
		buttonLoading.set(false);
	}
}

export async function getVehicleDetails(vehicleId) {
	try {
		const response = await client(`/v1/${vehicleId}/details`, {
			method: "GET",
			version: "Vehicle",
		});
		return response;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getCarActivities(sourceId, source, page) {
	try {
		const response = await client(`/v1/actionLogs/filter`, {
			method: "GET",
			version: "Vehicle",
			params: {
				sourceId,
				source,
				page,
			},
		});
		totalRecords.set(response.data.totalCount);
		totalPages.set(response.data.totalPage);
		initPage.set(response.data.pageNumber);
		return response;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function loadVehicleOptions(inputValue) {
	// only search if input has at least 3 characters
	if (!inputValue || inputValue.trim().length < 3) return [];
	try {
		const res = await client(`/v1/plateNumbers/${inputValue}?limit=100`, {
			method: "GET",
			version: "Vehicle",
		});
		return res.data.map((car) => ({
			value: car.vehicleId,
			label: car.plateNo,
		}));
	} catch (e) {
		console.error(e);
		return [];
	}
}

export async function getAssetOwner(vehicleId: string) {
	try {
		const res = await client(`/v1/${vehicleId}/details`, {
			method: "GET",
			version: "Vehicle",
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function editVehicleModel({
	id,
	name,
	vehicleTypeID,
	vehicleMakeID,
}) {
	try {
		const res = await client("/v1/models", {
			method: "PUT",
			version: "Vehicle",
			data: {
				name: name,
				vehicleTypeID: vehicleTypeID,
				vehicleMakeID: vehicleMakeID,
				id: id,
			},
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function addVehicleModel({ name, vehicleTypeID, vehicleMakeID }) {
	try {
		const res = await client("/v1/models", {
			method: "POST",
			version: "Vehicle",
			data: {
				name: name,
				vehicleTypeID: vehicleTypeID,
				vehicleMakeID: vehicleMakeID,
			},
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getVehicleModels(initPage) {
	try {
		const res = await client(`/v1/models/filter?Page=${initPage}&Size=50`, {
			method: "GET",
			version: "Vehicle",
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function deleteVehicleModel(id) {
	try {
		const res = await client(`/v1/models/${id}`, {
			method: "DELETE",
			version: "Vehicle",
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function changeVehicleModelStatus(id, status) {
	try {
		const res = await client("/v1/models/status", {
			method: "PUT",
			version: "Vehicle",
			data: {
				id,
				status,
			},
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getCarparkLocationsPopcar(postalCodeOrAddress: string) {
	try {
		const res = await client(`/v1/location`, {
			method: "GET",
			version: "Vehicle",
			params: {
				postalCodeOrAddress,
			},
		});
		return mapDataToAddressOptions(res.data);
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getEfficiencyRates(size = 100) {
	try {
		const first = await client(
			`/v1/fuel/efficiencyRate?Page=1&Size=${size}`,
			{
				method: "GET",
				version: "Vehicle",
			}
		);
		const totalPage: number = first.data.totalPage ?? 1;
		if (totalPage <= 1) {
			return first.data.dataList || [];
		}
		const rest = await Promise.all(
			Array.from({ length: totalPage - 1 }, (_, i) =>
				client(`/v1/fuel/efficiencyRate?Page=${i + 2}&Size=${size}`, {
					method: "GET",
					version: "Vehicle",
				})
			)
		);
		return [first, ...rest].flatMap((r) => r.data.dataList || []);
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getFilteredVehicles(queryParams: string) {
	try {
		const res = await client(`/v1/filter${queryParams}`, {
			method: "GET",
			version: "Vehicle",
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getCarparkLocationsOnemap(searchVal: string) {
	const filterText = searchVal ? searchVal.replace(" ", "_") : "";
	const res = await fetch(
		`https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${filterText}&returnGeom=Y&getAddrDetails=Y`
	);
	const data = await res.json();
	return mapDataToAddressOptions(data.results);
}
